import React, { useState, useEffect } from 'react';
import { getAllWebUsers, activateWebLicense, LICENSE_PLANS } from '../services/webLicenseService';
import { getDatabase, ref, get } from 'firebase/database';

const WebLicenseAdmin = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedPlan, setSelectedPlan] = useState('biannual');
    const [searchEmail, setSearchEmail] = useState('');
    const [searching, setSearching] = useState(false);
    const [foundUser, setFoundUser] = useState(null);
    const [activating, setActivating] = useState(false);
    const [message, setMessage] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [allEmails, setAllEmails] = useState([]);
    const [emailSuggestions, setEmailSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);

    useEffect(() => {
        loadUsers();
        loadAllEmails();
    }, []);

    const loadUsers = async () => {
        try {
            setLoading(true);
            const allUsers = await getAllWebUsers();
            setUsers(allUsers);
        } catch (error) {
            console.error('Error loading users:', error);
            setMessage({ type: 'error', text: 'Error al cargar usuarios' });
        } finally {
            setLoading(false);
        }
    };

    const loadAllEmails = async () => {
        try {
            const db = getDatabase();
            const usersRef = ref(db, 'users');
            const snapshot = await get(usersRef);

            if (snapshot.exists()) {
                const usersData = snapshot.val();
                const emails = Object.values(usersData)
                    .map(user => user.email)
                    .filter(email => email);
                setAllEmails(emails);
            }
        } catch (error) {
            console.error('Error loading emails:', error);
        }
    };

    const handleEmailInputChange = (value) => {
        setSearchEmail(value);
        setFoundUser(null);
        setSelectedSuggestionIndex(-1);

        if (value.trim().length >= 2) {
            const filtered = allEmails.filter(email =>
                email.toLowerCase().includes(value.toLowerCase())
            ).slice(0, 10);
            setEmailSuggestions(filtered);
            setShowSuggestions(filtered.length > 0);
        } else {
            setEmailSuggestions([]);
            setShowSuggestions(false);
        }
    };

    const handleSuggestionClick = (email) => {
        setSearchEmail(email);
        setShowSuggestions(false);
        setEmailSuggestions([]);
    };

    const handleKeyDown = (e) => {
        if (!showSuggestions) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedSuggestionIndex(prev =>
                prev < emailSuggestions.length - 1 ? prev + 1 : prev
            );
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedSuggestionIndex(prev => prev > 0 ? prev - 1 : -1);
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (selectedSuggestionIndex >= 0) {
                handleSuggestionClick(emailSuggestions[selectedSuggestionIndex]);
            } else {
                handleSearchRTDB();
            }
        } else if (e.key === 'Escape') {
            setShowSuggestions(false);
        }
    };

    const handleSearchRTDB = async () => {
        if (!searchEmail.trim()) return;
        setSearching(true);
        setFoundUser(null);
        setMessage(null);

        try {
            const db = getDatabase();
            const usersRef = ref(db, 'users');
            const snapshot = await get(usersRef);

            if (snapshot.exists()) {
                const usersData = snapshot.val();
                const matchedUser = Object.entries(usersData).find(
                    ([uid, userData]) => userData.email?.toLowerCase() === searchEmail.trim().toLowerCase()
                );

                if (matchedUser) {
                    const [uid, userData] = matchedUser;
                    const existingWebUser = users.find(u => u.userId === uid);
                    setFoundUser({
                        userId: uid,
                        email: userData.email,
                        displayName: userData.displayName || userData.fullName || 'Sin nombre',
                        hasWebLicense: !!existingWebUser
                    });
                } else {
                    setMessage({ type: 'error', text: 'No se encontró ningún usuario con ese correo en RTDB' });
                }
            }
        } catch (error) {
            console.error('Error buscando usuario:', error);
            setMessage({ type: 'error', text: 'Error al buscar usuario' });
        } finally {
            setSearching(false);
        }
    };

    const handleActivate = async (userId) => {
        if (!selectedPlan) {
            setMessage({ type: 'error', text: 'Por favor selecciona un plan' });
            return;
        }

        setActivating(true);
        setMessage(null);

        try {
            const result = await activateWebLicense(userId, selectedPlan);
            if (result.success) {
                setMessage({ type: 'success', text: 'Licencia activada exitosamente' });
                loadUsers();
                setSelectedUser(null);
                setFoundUser(null);
                setSearchEmail('');
            } else {
                setMessage({ type: 'error', text: result.error || 'Error al activar licencia' });
            }
        } catch (error) {
            setMessage({ type: 'error', text: 'Error al activar licencia' });
        } finally {
            setActivating(false);
        }
    };

    const filteredUsers = users.filter(user => {
        const email = user.email?.toLowerCase() || '';
        const userId = user.userId?.toLowerCase() || '';
        const search = searchTerm.toLowerCase();
        return email.includes(search) || userId.includes(search);
    });

    const formatDate = (timestamp) => {
        if (!timestamp) return '-';
        return new Date(timestamp).toLocaleDateString('es-MX', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const getDaysRemaining = (endDate) => {
        if (!endDate) return 0;
        const now = Date.now();
        const days = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
        return days;
    };

    const planOptions = Object.values(LICENSE_PLANS);

    return (
        <div style={{ background: 'rgba(255, 255, 255, 0.05)', borderRadius: '16px', padding: '2rem', minHeight: '80vh' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
                <h2 style={{ margin: 0, color: '#fff', fontSize: '1.5rem' }}>
                    🖥️ Administración de Licencias Web
                </h2>
                <button
                    onClick={loadUsers}
                    style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        border: 'none',
                        padding: '10px 20px',
                        color: 'white',
                        borderRadius: '8px',
                        fontSize: '0.9rem',
                        fontWeight: '600',
                        cursor: 'pointer'
                    }}
                >
                    🔄 Actualizar Lista
                </button>
            </div>

            {/* Sección de búsqueda RTDB */}
            <div style={{ background: 'rgba(102, 126, 234, 0.1)', border: '1px solid rgba(102, 126, 234, 0.3)', borderRadius: '12px', padding: '1.5rem', marginBottom: '1.5rem' }}>
                <h3 style={{ margin: '0 0 0.5rem 0', color: '#fff', fontSize: '1.1rem' }}>🔍 Buscar Usuario en RTDB</h3>
                <p style={{ margin: '0 0 1rem 0', color: '#94a3b8', fontSize: '0.85rem' }}>Busca usuarios registrados en la app para activarles una licencia web</p>

                <div style={{ position: 'relative', display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                    <div style={{ flex: 1, position: 'relative' }}>
                        <input
                            type="email"
                            placeholder="Email del usuario... (escribe al menos 2 caracteres)"
                            value={searchEmail}
                            onChange={(e) => handleEmailInputChange(e.target.value)}
                            onKeyDown={handleKeyDown}
                            onFocus={() => searchEmail.length >= 2 && emailSuggestions.length > 0 && setShowSuggestions(true)}
                            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                            style={{
                                width: '100%',
                                padding: '12px 16px',
                                borderRadius: '8px',
                                border: '1px solid rgba(255, 255, 255, 0.2)',
                                background: 'rgba(255, 255, 255, 0.05)',
                                color: '#fff',
                                fontSize: '0.9rem'
                            }}
                        />

                        {/* Dropdown de sugerencias */}
                        {showSuggestions && emailSuggestions.length > 0 && (
                            <div style={{
                                position: 'absolute',
                                top: '100%',
                                left: 0,
                                right: 0,
                                marginTop: '4px',
                                background: 'rgba(30, 30, 46, 0.98)',
                                border: '1px solid rgba(102, 126, 234, 0.3)',
                                borderRadius: '8px',
                                maxHeight: '300px',
                                overflowY: 'auto',
                                zIndex: 1000,
                                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)'
                            }}>
                                {emailSuggestions.map((email, index) => (
                                    <div
                                        key={email}
                                        onClick={() => handleSuggestionClick(email)}
                                        style={{
                                            padding: '10px 16px',
                                            cursor: 'pointer',
                                            background: index === selectedSuggestionIndex
                                                ? 'rgba(102, 126, 234, 0.3)'
                                                : 'transparent',
                                            color: '#fff',
                                            fontSize: '0.9rem',
                                            borderBottom: index < emailSuggestions.length - 1
                                                ? '1px solid rgba(255, 255, 255, 0.05)'
                                                : 'none',
                                            transition: 'background 0.15s'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.background = 'rgba(102, 126, 234, 0.2)';
                                        }}
                                        onMouseLeave={(e) => {
                                            if (index !== selectedSuggestionIndex) {
                                                e.currentTarget.style.background = 'transparent';
                                            }
                                        }}
                                    >
                                        📧 {email}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <button
                        onClick={handleSearchRTDB}
                        disabled={searching || !searchEmail}
                        style={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            border: 'none',
                            padding: '12px 24px',
                            color: 'white',
                            borderRadius: '8px',
                            fontSize: '0.9rem',
                            fontWeight: '600',
                            cursor: searching || !searchEmail ? 'not-allowed' : 'pointer',
                            opacity: searching || !searchEmail ? 0.5 : 1
                        }}
                    >
                        {searching ? 'Buscando...' : 'Buscar'}
                    </button>
                </div>

                {foundUser && (
                    <div style={{ background: 'rgba(255, 255, 255, 0.05)', borderRadius: '8px', padding: '1rem', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                        <h4 style={{ margin: '0 0 0.5rem 0', color: '#a5b4fc' }}>Usuario Encontrado</h4>
                        <p style={{ margin: '0.25rem 0', color: '#cbd5e1', fontSize: '0.9rem' }}><strong>Nombre:</strong> {foundUser.displayName}</p>
                        <p style={{ margin: '0.25rem 0', color: '#cbd5e1', fontSize: '0.9rem' }}><strong>Email:</strong> {foundUser.email}</p>
                        <p style={{ margin: '0.25rem 0 1rem 0', color: '#cbd5e1', fontSize: '0.9rem' }}><strong>ID:</strong> {foundUser.userId}</p>

                        {foundUser.hasWebLicense ? (
                            <div style={{ padding: '0.75rem', background: 'rgba(16, 185, 129, 0.2)', border: '1px solid #10b981', borderRadius: '6px', color: '#10b981', fontSize: '0.9rem' }}>
                                ✅ Este usuario ya tiene una licencia web activa
                            </div>
                        ) : (
                            <div>
                                <label style={{ display: 'block', marginBottom: '0.5rem', color: '#cbd5e1', fontSize: '0.9rem' }}>Seleccionar Plan:</label>
                                <select
                                    value={selectedPlan}
                                    onChange={(e) => setSelectedPlan(e.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        borderRadius: '6px',
                                        border: '1px solid rgba(255, 255, 255, 0.2)',
                                        background: 'rgba(255, 255, 255, 0.05)',
                                        color: '#fff',
                                        fontSize: '0.9rem',
                                        marginBottom: '1rem'
                                    }}
                                >
                                    {planOptions.map(plan => (
                                        <option key={plan.id} value={plan.id} style={{ background: '#1a1a2e' }}>
                                            {plan.name} - {plan.durationDays} días - ${plan.price} MXN
                                        </option>
                                    ))}
                                </select>
                                <button
                                    onClick={() => handleActivate(foundUser.userId)}
                                    disabled={activating}
                                    style={{
                                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                        border: 'none',
                                        padding: '12px 24px',
                                        color: 'white',
                                        borderRadius: '8px',
                                        fontSize: '0.9rem',
                                        fontWeight: '600',
                                        cursor: activating ? 'not-allowed' : 'pointer',
                                        opacity: activating ? 0.7 : 1
                                    }}
                                >
                                    {activating ? 'Activando...' : '✅ Activar Licencia Web'}
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {/* Opción de activación manual con email */}
                {!foundUser && searchEmail.trim() && !searching && (
                    <div style={{
                        background: 'rgba(251, 191, 36, 0.1)',
                        borderRadius: '8px',
                        padding: '1rem',
                        border: '1px solid rgba(251, 191, 36, 0.3)',
                        marginTop: '1rem'
                    }}>
                        <h4 style={{ margin: '0 0 0.5rem 0', color: '#fbbf24', fontSize: '0.95rem' }}>⚠️ Usuario no encontrado en RTDB</h4>
                        <p style={{ margin: '0 0 1rem 0', color: '#cbd5e1', fontSize: '0.85rem', lineHeight: '1.6' }}>
                            El email <strong>{searchEmail}</strong> no está registrado en la base de datos.
                            Puedes activar la licencia de todas formas para este email.
                        </p>
                        <div>
                            <label style={{ display: 'block', marginBottom: '0.5rem', color: '#cbd5e1', fontSize: '0.9rem' }}>Seleccionar Plan:</label>
                            <select
                                value={selectedPlan}
                                onChange={(e) => setSelectedPlan(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    borderRadius: '6px',
                                    border: '1px solid rgba(255, 255, 255, 0.2)',
                                    background: 'rgba(255, 255, 255, 0.05)',
                                    color: '#fff',
                                    fontSize: '0.9rem',
                                    marginBottom: '1rem'
                                }}
                            >
                                {planOptions.map(plan => (
                                    <option key={plan.id} value={plan.id} style={{ background: '#1a1a2e' }}>
                                        {plan.name} - {plan.durationDays} días - ${plan.price} MXN
                                    </option>
                                ))}
                            </select>
                            <button
                                onClick={() => {
                                    // Crear un usuario temporal con el email ingresado
                                    const tempUserId = `manual_${Date.now()}`;
                                    handleActivate(tempUserId);
                                }}
                                disabled={activating}
                                style={{
                                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                                    border: 'none',
                                    padding: '12px 24px',
                                    color: 'white',
                                    borderRadius: '8px',
                                    fontSize: '0.9rem',
                                    fontWeight: '600',
                                    cursor: activating ? 'not-allowed' : 'pointer',
                                    opacity: activating ? 0.7 : 1
                                }}
                            >
                                {activating ? 'Activando...' : '⚡ Activar Manualmente'}
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {message && (
                <div style={{
                    padding: '1rem',
                    borderRadius: '8px',
                    marginBottom: '1rem',
                    background: message.type === 'success' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                    border: `1px solid ${message.type === 'success' ? '#10b981' : '#ef4444'}`,
                    color: message.type === 'success' ? '#10b981' : '#ef4444'
                }}>
                    {message.text}
                    <button
                        onClick={() => setMessage(null)}
                        style={{
                            float: 'right',
                            background: 'none',
                            border: 'none',
                            color: 'inherit',
                            cursor: 'pointer',
                            fontSize: '1.2rem'
                        }}
                    >
                        ×
                    </button>
                </div>
            )}

            {/* Buscador de lista */}
            <div style={{ marginBottom: '1.5rem' }}>
                <input
                    type="text"
                    placeholder="Buscar por email o userId..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                        width: '100%',
                        maxWidth: '400px',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        background: 'rgba(255, 255, 255, 0.05)',
                        color: '#fff',
                        fontSize: '0.9rem'
                    }}
                />
            </div>

            {/* Panel de activación para usuarios existentes */}
            {selectedUser && (
                <div style={{
                    background: 'rgba(102, 126, 234, 0.1)',
                    border: '1px solid rgba(102, 126, 234, 0.3)',
                    borderRadius: '12px',
                    padding: '1.5rem',
                    marginBottom: '1.5rem'
                }}>
                    <h3 style={{ margin: '0 0 1rem 0', color: '#fff', fontSize: '1.1rem' }}>
                        Activar Licencia para: <span style={{ color: '#a5b4fc' }}>{selectedUser.email}</span>
                    </h3>

                    <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                        {planOptions.map((plan) => (
                            <button
                                key={plan.id}
                                onClick={() => setSelectedPlan(plan.id)}
                                style={{
                                    padding: '10px 20px',
                                    borderRadius: '8px',
                                    border: selectedPlan === plan.id ? '2px solid #667eea' : '1px solid rgba(255, 255, 255, 0.2)',
                                    background: selectedPlan === plan.id ? 'rgba(102, 126, 234, 0.3)' : 'rgba(255, 255, 255, 0.05)',
                                    color: '#fff',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s'
                                }}
                            >
                                {plan.name} - ${plan.price} MXN
                            </button>
                        ))}
                    </div>

                    <div style={{ display: 'flex', gap: '1rem' }}>
                        <button
                            onClick={() => handleActivate(selectedUser.userId)}
                            disabled={activating}
                            style={{
                                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                border: 'none',
                                padding: '12px 24px',
                                color: 'white',
                                borderRadius: '8px',
                                fontSize: '0.9rem',
                                fontWeight: '600',
                                cursor: activating ? 'not-allowed' : 'pointer',
                                opacity: activating ? 0.7 : 1
                            }}
                        >
                            {activating ? 'Activando...' : '✅ Activar Licencia'}
                        </button>
                        <button
                            onClick={() => setSelectedUser(null)}
                            style={{
                                background: 'transparent',
                                border: '1px solid rgba(255, 255, 255, 0.3)',
                                padding: '12px 24px',
                                color: '#fff',
                                borderRadius: '8px',
                                fontSize: '0.9rem',
                                cursor: 'pointer'
                            }}
                        >
                            Cancelar
                        </button>
                    </div>
                </div>
            )}

            {/* Tabla de usuarios */}
            {loading ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                    Cargando usuarios...
                </div>
            ) : filteredUsers.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                    No se encontraron usuarios web
                </div>
            ) : (
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', color: '#fff' }}>
                        <thead>
                            <tr style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                                <th style={{ padding: '12px', textAlign: 'left', color: '#94a3b8' }}>Email</th>
                                <th style={{ padding: '12px', textAlign: 'left', color: '#94a3b8' }}>Tipo Licencia</th>
                                <th style={{ padding: '12px', textAlign: 'left', color: '#94a3b8' }}>Inicio</th>
                                <th style={{ padding: '12px', textAlign: 'left', color: '#94a3b8' }}>Vence</th>
                                <th style={{ padding: '12px', textAlign: 'left', color: '#94a3b8' }}>Días Restantes</th>
                                <th style={{ padding: '12px', textAlign: 'center', color: '#94a3b8' }}>Estado</th>
                                <th style={{ padding: '12px', textAlign: 'center', color: '#94a3b8' }}>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredUsers.map((user) => {
                                const license = user.license || {};
                                const isValid = license.isActive && license.endDate > Date.now();
                                const daysRemaining = getDaysRemaining(license.endDate);

                                return (
                                    <tr key={user.userId} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                                        <td style={{ padding: '12px' }}>
                                            <div style={{ fontWeight: '500' }}>{user.email || '-'}</div>
                                            <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                                                {user.userId?.substring(0, 8)}...
                                            </div>
                                        </td>
                                        <td style={{ padding: '12px' }}>
                                            {license.type ? LICENSE_PLANS[license.type]?.name || license.type : '-'}
                                        </td>
                                        <td style={{ padding: '12px' }}>{formatDate(license.startDate)}</td>
                                        <td style={{ padding: '12px' }}>{formatDate(license.endDate)}</td>
                                        <td style={{
                                            padding: '12px',
                                            color: daysRemaining <= 7 ? '#ef4444' : daysRemaining <= 30 ? '#f59e0b' : '#10b981'
                                        }}>
                                            {license.endDate ? (daysRemaining > 0 ? `${daysRemaining} días` : 'Vencida') : '-'}
                                        </td>
                                        <td style={{ padding: '12px', textAlign: 'center' }}>
                                            <span style={{
                                                display: 'inline-block',
                                                padding: '4px 12px',
                                                borderRadius: '20px',
                                                fontSize: '0.75rem',
                                                fontWeight: '600',
                                                background: isValid ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                                                color: isValid ? '#10b981' : '#ef4444'
                                            }}>
                                                {isValid ? 'Activa' : 'Inactiva'}
                                            </span>
                                        </td>
                                        <td style={{ padding: '12px', textAlign: 'center' }}>
                                            <button
                                                onClick={() => setSelectedUser(user)}
                                                style={{
                                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                    border: 'none',
                                                    padding: '8px 16px',
                                                    color: 'white',
                                                    borderRadius: '6px',
                                                    fontSize: '0.8rem',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                Activar/Renovar
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Resumen */}
            <div style={{
                marginTop: '1.5rem',
                padding: '1rem',
                background: 'rgba(255, 255, 255, 0.03)',
                borderRadius: '8px',
                display: 'flex',
                gap: '2rem',
                flexWrap: 'wrap'
            }}>
                <div>
                    <span style={{ color: '#64748b', fontSize: '0.85rem' }}>Total Usuarios Web: </span>
                    <span style={{ color: '#fff', fontWeight: '600' }}>{users.length}</span>
                </div>
                <div>
                    <span style={{ color: '#64748b', fontSize: '0.85rem' }}>Licencias Activas: </span>
                    <span style={{ color: '#10b981', fontWeight: '600' }}>
                        {users.filter(u => u.license?.isActive && u.license?.endDate > Date.now()).length}
                    </span>
                </div>
                <div>
                    <span style={{ color: '#64748b', fontSize: '0.85rem' }}>Licencias Vencidas: </span>
                    <span style={{ color: '#ef4444', fontWeight: '600' }}>
                        {users.filter(u => !u.license?.isActive || !u.license?.endDate || u.license?.endDate <= Date.now()).length}
                    </span>
                </div>
            </div>
        </div>
    );
};

export default WebLicenseAdmin;
